import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useNavigate } from 'react-router-dom';
import { useDeleteVoucher } from 'src/features/vouchers/api/useDeleteVoucher';
import { LIST_VOUCHERS_RESOURCE } from 'src/features/vouchers/api/useListVouchers';
import { vouchersMessages } from 'src/features/vouchers/utils/messages';
import { Voucher } from 'src/features/vouchers/utils/types';

interface DeleteVoucherFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedVoucher: Voucher;
    children?: React.ReactNode;
}

function DeleteVoucherFormModal<TData>({ selectedVoucher, children }: DeleteVoucherFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const { mutate: deleteVoucher } = useDeleteVoucher({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_VOUCHERS_RESOURCE] });
            setIsModalOpen(false);
            navigate('/vouchers');
            toast({
                title: formatMessage(vouchersMessages.deleteVoucherSuccess),
                description: formatMessage(vouchersMessages.deleteVoucherSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...vouchersMessages.deleteVoucher },
                subTitle: {
                    ...vouchersMessages.deleteVoucherSubtitle,
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...vouchersMessages.confirmDeleteVoucher },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: vouchersMessages.deleteVoucher,
                callbackFn: () => {
                    return (
                        deleteVoucher({
                            voucherId: selectedVoucher.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteVoucherFormModal;
